// src/pages/SocialConsentPage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import api, { setAccessToken } from '../api/axiosConfig';
import { flushNow, linkAnalyticsSession, trackUxEvent } from '../analytics/analytics';
import { useAppDispatch } from '../store/hooks';
import { loginSuccess } from '../store/slice/authSlice';

type ConsentCompleteResponse = {
  accessToken?: string;
  token?: string;
  refreshToken?: string;
  userId?: number;
  id?: number;
  email?: string;
  consentRequired?: boolean;
};

type ConsentState = {
  agreeTerms: boolean;
  agreePrivacy: boolean;
  agreeDisclaimer: boolean;
  ageOver14Agree: boolean;
  marketingEmailAgree: boolean;
  marketingSmsAgree: boolean;
};

const INITIAL_CONSENT: ConsentState = {
  agreeTerms: false,
  agreePrivacy: false,
  agreeDisclaimer: false,
  ageOver14Agree: false,
  marketingEmailAgree: false,
  marketingSmsAgree: false,
};

function resolveTokenExpiration(token: string): number {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) {
      return Date.now() + 30 * 60 * 1000;
    }

    const normalized = payloadPart
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const padded =
      normalized + '='.repeat((4 - (normalized.length % 4)) % 4);

    const payload = JSON.parse(window.atob(padded)) as {
      exp?: number;
    };

    if (payload.exp && Number.isFinite(payload.exp)) {
      return payload.exp * 1000;
    }
  } catch {
    // JWT 만료시각 파싱에 실패하면 안전한 기본값을 사용한다.
  }

  return Date.now() + 30 * 60 * 1000;
}

function resolveErrorMessage(error: any): string {
  const status = error?.response?.status;
  const responseMessage = error?.response?.data?.message;

  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return responseMessage;
  }

  if (status === 400) {
    return '필수 동의 항목을 모두 확인해주세요.';
  }

  if (status === 401) {
    return '동의 인증 정보가 만료되었습니다. 소셜 로그인을 다시 진행해주세요.';
  }

  if (status === 403) {
    return '현재 계정으로 동의를 완료할 수 없습니다.';
  }

  return '동의 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
}

const SocialConsentPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [consent, setConsent] =
    useState<ConsentState>(INITIAL_CONSENT);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(
    null,
  );

  const openedAtRef = useRef(Date.now());
  const finishedRef = useRef(false);
  const viewTrackedRef = useRef(false);

  const allRequiredAgreed = useMemo(
    () =>
      consent.agreeTerms &&
      consent.agreePrivacy &&
      consent.agreeDisclaimer &&
      consent.ageOver14Agree,
    [
      consent.agreeTerms,
      consent.agreePrivacy,
      consent.agreeDisclaimer,
      consent.ageOver14Agree,
    ],
  );

  const allAgreed = useMemo(
    () =>
      allRequiredAgreed &&
      consent.marketingEmailAgree &&
      consent.marketingSmsAgree,
    [
      allRequiredAgreed,
      consent.marketingEmailAgree,
      consent.marketingSmsAgree,
    ],
  );


  useEffect(() => {
    // React.StrictMode의 effect 재실행에서도 view 이벤트는 한 번만 기록한다.
    if (!viewTrackedRef.current) {
      viewTrackedRef.current = true;
      try {
        trackUxEvent('social_consent_view', {
          requiredCount: 4,
          marketingOptional: true,
        });
      } catch {
        // analytics failure must never block consent UI.
      }
    }

    const handlePageHide = () => {
      if (finishedRef.current) return;

      try {
        trackUxEvent('social_consent_exit', {
          elapsedMs: Math.max(0, Date.now() - openedAtRef.current),
        });
        flushNow();
      } catch {
        // analytics lifecycle failure must never block navigation.
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);

  const setField = (
    field: keyof ConsentState,
    checked: boolean,
  ) => {
    setConsent((previous) => ({
      ...previous,
      [field]: checked,
    }));
    setErrorMessage(null);
  };

  const handleAllAgree = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;

    setConsent({
      agreeTerms: checked,
      agreePrivacy: checked,
      agreeDisclaimer: checked,
      ageOver14Agree: checked,
      marketingEmailAgree: checked,
      marketingSmsAgree: checked,
    });
    setErrorMessage(null);
  };

  const handleSubmit = async () => {
    if (!allRequiredAgreed || submitting) {
      const message =
        '필수 항목(이용약관, 개인정보 처리방침, 서비스 고지, 만 14세 이상)에 모두 동의해주세요.';

      setErrorMessage(message);
      try {
        trackUxEvent('social_consent_validation_error', {
          reason: 'required-consent-missing',
          requiredAgreed: allRequiredAgreed,
          marketingEmailAgree: consent.marketingEmailAgree,
          marketingSmsAgree: consent.marketingSmsAgree,
        });
      } catch {
        // analytics failure must never block validation feedback.
      }
      return;
    }

    try {
      trackUxEvent('social_consent_submit', {
        requiredAgreed: true,
        marketingEmailAgree: consent.marketingEmailAgree,
        marketingSmsAgree: consent.marketingSmsAgree,
        elapsedMs: Math.max(0, Date.now() - openedAtRef.current),
      });
    } catch {
      // analytics failure must never block consent submission.
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const response =
        await api.post<ConsentCompleteResponse>(
          '/auth/consent/complete',
          consent,
        );

      const data = response.data;
      const token = data.accessToken ?? data.token;
      const userId = data.userId ?? data.id;
      const email = data.email;

      if (
        !token ||
        !userId ||
        !email ||
        data.consentRequired === true
      ) {
        throw new Error(
          '동의 완료 후 로그인 정보가 정상적으로 발급되지 않았습니다.',
        );
      }

      const expiresAt = resolveTokenExpiration(token);

      // analytics/link-session이 즉시 JWT를 사용할 수 있도록 Redux 반영 전에
      // 공통 tokenStore에도 access token을 먼저 연결한다.
      setAccessToken(token, expiresAt);

      // 서버 동의 완료 + access token 확보 시점부터 인증 핵심 흐름은 성공이다.
      // 이후 Analytics는 어떤 오류가 나도 이 성공 상태를 뒤집지 않는다.
      finishedRef.current = true;

      try {
        const elapsedMs = Math.max(0, Date.now() - openedAtRef.current);

        trackUxEvent('social_consent_success', {
          marketingEmailAgree: consent.marketingEmailAgree,
          marketingSmsAgree: consent.marketingSmsAgree,
          elapsedMs,
        });

        // 신규 소셜 계정은 consent 완료가 실제 가입 완료 authority다.
        // 기존에는 auth_success만 남아 organic-v2의 signup_success 단계가 비어 있었다.
        trackUxEvent('signup_success', {
          provider: 'social',
          source: 'social-consent',
          signup_flow: 'social_consent',
          user_id_present: true,
          elapsedMs,
        });
        flushNow();
      } catch {
        // analytics failure must never block consent completion.
      }

      try {
        void linkAnalyticsSession().catch(() => {
          // link 실패는 데이터 연결만 누락시키고 로그인은 계속한다.
        });
      } catch {
        // 동기 예외까지 fail-open 처리한다.
      }

      dispatch(
        loginSuccess({
          id: userId,
          email,
          token,
          expiresAt,
        }),
      );

      navigate('/', {
        replace: true,
      });
    } catch (error: any) {
      const message = resolveErrorMessage(error);
      setErrorMessage(message);

      try {
        trackUxEvent('social_consent_api_error', {
          status: typeof error?.response?.status === 'number'
            ? error.response.status
            : null,
          message,
          elapsedMs: Math.max(0, Date.now() - openedAtRef.current),
        });
      } catch {
        // analytics failure must never hide the real consent error.
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestartLogin = () => {
    finishedRef.current = true;
    try {
      trackUxEvent('social_consent_cancel', {
        elapsedMs: Math.max(0, Date.now() - openedAtRef.current),
        requiredAgreed: allRequiredAgreed,
        marketingEmailAgree: consent.marketingEmailAgree,
        marketingSmsAgree: consent.marketingSmsAgree,
      });
      flushNow();
    } catch {
      // analytics failure must never block cancel navigation.
    }

    navigate('/', {
      replace: true,
    });
  };

  return (
    <Box
      sx={{
        minHeight: {
          xs: 'calc(100vh - 130px)',
          md: 'calc(100vh - 150px)',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: {
          xs: 2,
          md: 5,
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 640,
          borderRadius: 3,
          border: '1px solid rgba(127,183,255,.22)',
          bgcolor: '#0a1320',
          color: '#eaf2ff',
          overflow: 'hidden',
          boxShadow: '0 24px 70px rgba(0,0,0,.32)',
        }}
      >
        <Box
          sx={{
            px: {
              xs: 2.2,
              sm: 4,
            },
            pt: {
              xs: 3,
              sm: 4,
            },
            pb: 2.5,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: 24,
                sm: 28,
              },
              fontWeight: 900,
              letterSpacing: '-0.03em',
            }}
          >
            서비스 이용 동의
          </Typography>

          <Typography
            sx={{
              mt: 1,
              color: '#9fb4d8',
              fontSize: 14,
              lineHeight: 1.7,
            }}
          >
            소셜 회원가입을 완료하려면 필수 항목에
            동의해야 합니다. 마케팅 수신 동의는 선택
            항목이며 동의하지 않아도 서비스를 이용할 수
            있습니다.
          </Typography>
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(127,183,255,.15)',
          }}
        />

        <Stack
          spacing={1}
          sx={{
            px: {
              xs: 2.2,
              sm: 4,
            },
            py: 3,
          }}
        >
          <Box
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(127,183,255,.26)',
              bgcolor: 'rgba(127,183,255,.06)',
              px: 1.5,
              py: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allAgreed}
                  onChange={handleAllAgree}
                  sx={{
                    color: '#7fb7ff',
                    '&.Mui-checked': {
                      color: '#7fb7ff',
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: 15,
                  }}
                >
                  전체 동의
                </Typography>
              }
            />
          </Box>

          <Divider
            sx={{
              my: 1,
              borderColor: 'rgba(127,183,255,.12)',
            }}
          />

          <ConsentCheck
            checked={consent.agreeTerms}
            onChange={(checked) =>
              setField('agreeTerms', checked)
            }
            label="[필수] 이용약관 동의"
          />

          <ConsentCheck
            checked={consent.agreePrivacy}
            onChange={(checked) =>
              setField('agreePrivacy', checked)
            }
            label="[필수] 개인정보 처리방침 동의"
          />

          <ConsentCheck
            checked={consent.agreeDisclaimer}
            onChange={(checked) =>
              setField('agreeDisclaimer', checked)
            }
            label="[필수] 서비스 고지 및 투자 유의사항 동의"
          />

          <ConsentCheck
            checked={consent.ageOver14Agree}
            onChange={(checked) =>
              setField('ageOver14Agree', checked)
            }
            label="[필수] 만 14세 이상 확인"
          />

          <Divider
            sx={{
              my: 1,
              borderColor: 'rgba(127,183,255,.12)',
            }}
          />

          <ConsentCheck
            checked={consent.marketingEmailAgree}
            onChange={(checked) =>
              setField('marketingEmailAgree', checked)
            }
            label="[선택] 이메일 마케팅 정보 수신 동의"
            optional
          />

          <ConsentCheck
            checked={consent.marketingSmsAgree}
            onChange={(checked) =>
              setField('marketingSmsAgree', checked)
            }
            label="[선택] SMS 마케팅 정보 수신 동의"
            optional
          />

          {errorMessage && (
            <Alert
              severity="error"
              sx={{
                mt: 1.5,
              }}
            >
              {errorMessage}
            </Alert>
          )}

          <Button
            type="button"
            fullWidth
            variant="contained"
            disabled={!allRequiredAgreed || submitting}
            onClick={handleSubmit}
            sx={{
              mt: 2,
              minHeight: 50,
              borderRadius: 2,
              fontWeight: 900,
              bgcolor: '#2962ff',
              '&:hover': {
                bgcolor: '#1f52db',
              },
            }}
          >
            {submitting ? (
              <CircularProgress
                size={23}
                color="inherit"
              />
            ) : (
              '동의하고 가입 완료'
            )}
          </Button>

          <Button
            type="button"
            fullWidth
            variant="text"
            disabled={submitting}
            onClick={handleRestartLogin}
            sx={{
              color: '#9fb4d8',
            }}
          >
            취소하고 처음으로
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

type ConsentCheckProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  optional?: boolean;
};

const ConsentCheck: React.FC<ConsentCheckProps> = ({
  checked,
  onChange,
  label,
  optional = false,
}) => {
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        px: 0.6,
        '&:hover': {
          bgcolor: 'rgba(255,255,255,.025)',
        },
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={(event) =>
              onChange(event.target.checked)
            }
            sx={{
              color: optional ? '#9fb4d8' : '#7fb7ff',
              '&.Mui-checked': {
                color: '#7fb7ff',
              },
            }}
          />
        }
        label={
          <Typography
            sx={{
              color: optional ? '#c3d4f1' : '#eaf2ff',
              fontSize: {
                xs: 13.5,
                sm: 14.5,
              },
              fontWeight: optional ? 500 : 700,
              lineHeight: 1.5,
            }}
          >
            {label}
          </Typography>
        }
      />
    </Box>
  );
};

export default SocialConsentPage;
